import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { User, Mail, Calendar, Settings } from 'lucide-react';
import { ProfileEditModal } from './ProfileEditModal';

export default function Profile() {
  const { user } = useAuth();
  const [editModalOpen, setEditModalOpen] = React.useState(false);

  if (!user) {
    return null;
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="border-b border-[#e6e6ea] px-5 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-[18px] font-semibold text-[#22212d]">Profile</h1>
          <Button onClick={() => setEditModalOpen(true)} variant="gradient" size="sm" className="h-7 px-2.5 text-[13px]">
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            Edit Profile
          </Button>
        </div>
      </div>

      {/* Page Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-6">
        <div className="grid gap-6">
          <Card className="bg-white border-[#e6e6ea]">
            <CardHeader className="pb-4">
              <CardTitle className="text-[16px] font-semibold text-[#22212d]">User Information</CardTitle>
              <CardDescription className="text-[13px] text-[rgba(34,33,45,0.6)]">Your personal details and account information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                <Avatar className="h-20 w-20 bg-[hsl(var(--avatar-bg))]">
                  <AvatarFallback className="text-[18px] text-white">
                    {getInitials(user.name || user.email)}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-2 text-[rgba(34,33,45,0.6)]">
                    <User className="h-4 w-4" />
                    <span className="text-[13px]">Name</span>
                    <span className="text-[#22212d] font-medium ml-auto text-[13px]">{user.name || 'Not set'}</span>
                  </div>

                  <div className="flex items-center gap-2 text-[rgba(34,33,45,0.6)]">
                    <Mail className="h-4 w-4" />
                    <span className="text-[13px]">Email</span>
                    <span className="text-[#22212d] font-medium ml-auto text-[13px]">{user.email}</span>
                  </div>

                  <div className="flex items-center gap-2 text-[rgba(34,33,45,0.6)]">
                    <Calendar className="h-4 w-4" />
                    <span className="text-[13px]">Joined</span>
                    <span className="text-[#22212d] font-medium ml-auto text-[13px]">
                      {formatDate(user.created_at || new Date().toISOString())}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#e6e6ea]">
            <CardHeader className="pb-4">
              <CardTitle className="text-[16px] font-semibold text-[#22212d]">Account Statistics</CardTitle>
              <CardDescription className="text-[13px] text-[rgba(34,33,45,0.6)]">Your usage and activity on the platform</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-[#f3f3f6]">
                  <div className="text-[22px] font-semibold text-[#22212d]">0</div>
                  <div className="text-[13px] text-[rgba(34,33,45,0.6)]">Total Queries</div>
                </div>
                <div className="p-4 rounded-lg bg-[#f3f3f6]">
                  <div className="text-[22px] font-semibold text-[#22212d]">0</div>
                  <div className="text-[13px] text-[rgba(34,33,45,0.6)]">Conversations</div>
                </div>
                <div className="p-4 rounded-lg bg-[#f3f3f6]">
                  <div className="text-[22px] font-semibold text-[#22212d]">0</div>
                  <div className="text-[13px] text-[rgba(34,33,45,0.6)]">Connections</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <ProfileEditModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
      />
    </div>
  );
}